<template>

   <nav v-if="breadcrump" class="flex" aria-label="Breadcrumb">
      <ol role="list" class="px-4 sm:px-6 lg:px-24 flex items-center space-x-4">
         <li>
            <div>
               <div @click="router.push({name: 'Home'})" class="text-gray-400 :hover:text-gray-500 cursor-pointer">
                  <font-awesome-icon size="lg" icon="fa-solid fa-home" class="text-gray-500" />
                  <span class="sr-only">Home</span>
               </div>
            </div>
         </li>
         <li v-for="item in breadcrump" :key="item.title">
            <div class="flex items-center">
               <svg class="h-5 w-5 flex-shrink-0 text-gray-300" fill="currentColor" viewBox="0 0 20 20" aria-hidden="true">
                  <path d="M5.555 17.776l8-16 .894.448-8 16-.894-.448z" />
               </svg>
               <div @click="item.link ? router.push(item.link) : null" :class="item.link ? 'cursor-pointer hover:text-gray-500' : ''" class="ml-4 text-sm font-medium text-gray-500">{{ item.title }}</div>
            </div>
         </li>
      </ol>
   </nav>
   <div class="py-10 px-4 sm:px-6 lg:px-24">
      <main class="flex flex-col gap-8">
         <!-- Premium Time Duration controls -->
         <div class="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-2 bg-white p-5 rounded-2xl border border-gray-100 shadow-sm">
            <div>
               <h1 class="text-xl font-bold text-gray-900">Dashboard Overview</h1>
               <p class="text-xs font-semibold text-gray-500 mt-1">Real-time performance metrics and bookings overview</p>
            </div>
            <div class="flex flex-wrap items-center gap-3">
               <div class="inline-flex rounded-xl bg-gray-50 p-1 border border-gray-200">
                  <button v-for="item in nonCustomScopes" 
                          :key="item.id" 
                          @click="handleScopeUpdate(item)" 
                          :class="item.isCurrent ? 'bg-white text-theme-active shadow-sm font-bold' : 'text-gray-600 hover:text-gray-900 font-semibold'" 
                          class="px-3.5 py-1.5 text-xs rounded-lg transition-all duration-200">
                     {{ item.name }}
                  </button>
               </div>
               <!-- Custom Date Range -->
               <div class="relative min-w-[200px]">
                  <span class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none text-gray-400">
                     <i class="pi pi-calendar text-xs"></i>
                  </span>
                  <flat-pickr v-model="dates" :config="dateRangeConfig" 
                              :class="isCustomScopeCurrent ? 'border-theme-active ring-2 ring-theme-active/10 text-theme-active font-bold' : 'border-gray-200 text-gray-700'" 
                              class="pl-8 pr-4 py-2 border rounded-xl text-xs w-full bg-white focus:outline-none focus:ring-2 focus:ring-theme-active focus:border-transparent transition-all duration-200" 
                              placeholder="Custom date range..." />
               </div>
            </div>
         </div>

         <!-- Gorgeous Loyalty Reward Card -->
         <div v-if="auth && auth.user && auth.user.type == 'agent' && auth.user.sub_type == null && loyaltyData" 
              class="bg-gradient-to-r from-emerald-800 to-teal-950 rounded-3xl p-6 text-white shadow-lg border border-teal-700/30 flex flex-col md:flex-row items-center justify-between gap-6 relative overflow-hidden">
            <div class="absolute -right-16 -top-16 w-48 h-48 bg-teal-500/10 rounded-full blur-3xl pointer-events-none"></div>
            <div class="absolute -left-16 -bottom-16 w-48 h-48 bg-emerald-500/10 rounded-full blur-3xl pointer-events-none"></div>
            
            <div class="flex-grow w-full md:w-auto">
               <div class="flex items-center gap-2 mb-1">
                  <i class="pi pi-gift text-xl text-emerald-400"></i>
                  <h3 class="text-lg font-extrabold tracking-wide uppercase">Loyalty Program Reward</h3>
               </div>
               <p class="text-xs text-emerald-300 font-semibold mb-3">
                  * Loyalty Program will be calculated from 01-07-2026
               </p>
               <p class="text-sm text-teal-100 font-semibold leading-relaxed max-w-2xl">
                  You have accumulated <span class="text-white font-extrabold text-lg px-1.5 py-0.5 rounded bg-white/10">{{ loyaltyData.points_earned }}</span> points 
                  (<span class="text-emerald-300 font-bold">${{ (Number(loyaltyData.points_earned) * (Number(loyaltyData.redeem_rate) || 0.1)).toFixed(2) }} USD</span>). 
                  Reach the target of <span class="font-extrabold">{{ loyaltyData.total_points }}</span> points 
                  (<span class="text-teal-300 font-bold">${{ (Number(loyaltyData.total_points) * (Number(loyaltyData.redeem_rate) || 0.1)).toFixed(2) }} USD</span>) to request a withdrawal!
               </p>
               <div class="mt-5 w-full bg-teal-950/50 rounded-full h-3 border border-teal-800/30">
                  <div class="bg-gradient-to-r from-emerald-400 to-teal-300 h-full rounded-full transition-all duration-500 shadow-[0_0_8px_rgba(52,211,153,0.5)]" 
                       :style="{ width: `${Math.min(100, ((Number(loyaltyData.points_earned) || 0) / Math.max(1, Number(loyaltyData.total_points) || 1)) * 100)}%` }"></div>
               </div>
               <div class="flex justify-between items-center mt-2 text-xs font-semibold text-teal-300">
                  <span>0 Points</span>
                  <span>{{ Math.min(100, Math.round(((Number(loyaltyData.points_earned) || 0) / Math.max(1, Number(loyaltyData.total_points) || 1)) * 100)) }}% Completed</span>
                  <span>{{ loyaltyData.total_points }} Points Target</span>
               </div>
            </div>
            
            <div class="flex-shrink-0 w-full md:w-auto text-right">
               <!-- 1. Processing request -->
               <button v-if="loyaltyData.pending_withdraw" 
                       disabled
                       class="w-full md:w-auto px-6 py-3.5 bg-emerald-900/50 border border-emerald-700/50 text-emerald-300/80 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                  Withdrawal Processing
               </button>
               <!-- 2. Reached threshold and can withdraw -->
               <button v-else-if="Number(loyaltyData.points_earned) >= Number(loyaltyData.total_points)" 
                       @click="handleRedeemPoints" 
                       :disabled="isProfileLoading"
                       class="w-full md:w-auto px-6 py-3.5 bg-white text-emerald-900 hover:bg-emerald-50 active:scale-[0.98] transition-all duration-200 font-extrabold text-sm rounded-xl shadow-md uppercase tracking-wider min-w-[160px] text-center flex items-center justify-center">
                  <span v-if="isProfileLoading" class="flex items-center gap-2">
                     <svg class="animate-spin h-4 w-4 text-emerald-900" fill="none" viewBox="0 0 24 24">
                        <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                        <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                     </svg>
                     Loading...
                  </span>
                  <span v-else>Withdraw Now</span>
               </button>
               <!-- 3. Under threshold - Faded / Disabled -->
               <button v-else
                       disabled
                       class="w-full md:w-auto px-6 py-3.5 bg-white/20 text-white/50 border border-white/10 font-extrabold text-sm rounded-xl cursor-not-allowed uppercase tracking-wider">
                  Withdraw Now
               </button>
            </div>
         </div>

         <!-- Reservations Summary Grid -->
         <div>
            <div class="mb-4">
               <h3 class="text-base font-bold text-gray-900">Reservations Summary</h3>
               <p class="text-xs font-semibold text-gray-500 mt-0.5">Quick lookup of your booking status counts</p>
            </div>
            <dl class="grid grid-cols-1 gap-5 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
               <!-- 1. Total Bookings -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-indigo-100 hover:scale-[1.01] transition-all duration-300">
                  <div class="flex items-center gap-4">
                     <div class="rounded-xl bg-indigo-50 text-indigo-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-calendar text-lg font-bold"></i>
                     </div>
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ bookingStats.total.name }}</p>
                        <p class="text-3xl font-black text-gray-900 mt-1">{{ bookingStats.total.stat }}</p>
                     </div>
                  </div>
                  <div @click="redirectBookings()" class="cursor-pointer border-t border-gray-50 mt-4 pt-3 flex items-center justify-between text-xs font-bold text-indigo-600 group-hover:text-indigo-700 transition-colors">
                     <span>View Details</span>
                     <i class="pi pi-arrow-right text-[10px]"></i>
                  </div>
               </div>

               <!-- 2. Confirmed Bookings -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-emerald-100 hover:scale-[1.01] transition-all duration-300">
                  <div class="flex items-center gap-4">
                     <div class="rounded-xl bg-emerald-50 text-emerald-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-check-circle text-lg font-bold"></i>
                     </div>
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ bookingStats.confirmed.name }}</p>
                        <p class="text-3xl font-black text-gray-900 mt-1">{{ bookingStats.confirmed.stat }}</p>
                     </div>
                  </div>
                  <div @click="redirectBookings('confirmed')" class="cursor-pointer border-t border-gray-50 mt-4 pt-3 flex items-center justify-between text-xs font-bold text-emerald-600 group-hover:text-emerald-700 transition-colors">
                     <span>View Details</span>
                     <i class="pi pi-arrow-right text-[10px]"></i>
                  </div>
               </div>

               <!-- 3. Pending Bookings -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-amber-100 hover:scale-[1.01] transition-all duration-300">
                  <div class="flex items-center gap-4">
                     <div class="rounded-xl bg-amber-50 text-amber-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-clock text-lg font-bold"></i>
                     </div>
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ bookingStats.pending.name }}</p>
                        <p class="text-3xl font-black text-gray-900 mt-1">{{ bookingStats.pending.stat }}</p>
                     </div>
                  </div>
                  <div @click="redirectBookings('awaiting_payment')" class="cursor-pointer border-t border-gray-50 mt-4 pt-3 flex items-center justify-between text-xs font-bold text-amber-600 group-hover:text-amber-700 transition-colors">
                     <span>View Details</span>
                     <i class="pi pi-arrow-right text-[10px]"></i>
                  </div>
               </div>

               <!-- 4. Sub Agent (Online Bookings) -->
               <div v-if="user.type == 'agent' && user.sub_type == null" 
                    class="group relative overflow-hidden rounded-2xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-purple-100 hover:scale-[1.01] transition-all duration-300">
                  <div class="flex items-center gap-4">
                     <div class="rounded-xl bg-purple-50 text-purple-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-users text-lg font-bold"></i>
                     </div>
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ bookingStats.subAgents.name }}</p>
                        <p class="text-3xl font-black text-gray-900 mt-1">{{ bookingStats.subAgents.stat }}</p>
                     </div>
                  </div>
                  <div @click="redirectBookings()" class="cursor-pointer border-t border-gray-50 mt-4 pt-3 flex items-center justify-between text-xs font-bold text-purple-600 group-hover:text-purple-700 transition-colors">
                     <span>View Details</span>
                     <i class="pi pi-arrow-right text-[10px]"></i>
                  </div>
               </div>
               
               <!-- 5. Cancelled Bookings -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-5 border border-gray-100 shadow-sm hover:shadow-md hover:border-rose-100 hover:scale-[1.01] transition-all duration-300">
                  <div class="flex items-center gap-4">
                     <div class="rounded-xl bg-rose-50 text-rose-600 p-3.5 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-times-circle text-lg font-bold"></i>
                     </div>
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ bookingStats.cancelled.name }}</p>
                        <p class="text-3xl font-black text-gray-900 mt-1">{{ bookingStats.cancelled.stat }}</p>
                     </div>
                  </div>
                  <div @click="redirectBookings('cancelled')" class="cursor-pointer border-t border-gray-50 mt-4 pt-3 flex items-center justify-between text-xs font-bold text-rose-600 group-hover:text-rose-700 transition-colors">
                     <span>View Details</span>
                     <i class="pi pi-arrow-right text-[10px]"></i>
                  </div>
               </div>
            </dl>
         </div>

         <!-- Financial Sales Summary -->
         <div v-if="user.type !== 'web'">
            <div class="mb-4">
               <h3 class="text-base font-bold text-gray-900">Financial Summary</h3>
               <p class="text-xs font-semibold text-gray-500 mt-0.5">Summary of total revenue and booking values</p>
            </div>
            <dl class="grid grid-cols-1 gap-5 md:grid-cols-2">
               <!-- Total Sales Card -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <div class="flex items-center justify-between">
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ salesStats.total.name }}</p>
                        <p class="text-3xl font-black text-theme-active mt-2">${{ salesStats.total.stat }}</p>
                     </div>
                     <div class="rounded-2xl bg-emerald-50 text-theme-active p-4 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-wallet text-xl"></i>
                     </div>
                  </div>
                  <div class="mt-4 text-xs font-semibold text-gray-400 flex items-center gap-1">
                     <i class="pi pi-info-circle text-[10px]"></i>
                     <span>Includes completed and pending collection orders</span>
                  </div>
               </div>

               <!-- Avg Booking Value Card -->
               <div class="group relative overflow-hidden rounded-2xl bg-white p-6 border border-gray-100 shadow-sm hover:shadow-md transition-all duration-300">
                  <div class="flex items-center justify-between">
                     <div>
                        <p class="text-xs font-bold text-gray-400 uppercase tracking-wider">{{ salesStats.average.name }}</p>
                        <p class="text-3xl font-black text-theme-active mt-2">${{ salesStats.average.stat }}</p>
                     </div>
                     <div class="rounded-2xl bg-indigo-50 text-indigo-600 p-4 group-hover:scale-110 transition-all duration-300">
                        <i class="pi pi-percentage text-xl"></i>
                     </div>
                  </div>
                  <div class="mt-4 text-xs font-semibold text-gray-400 flex items-center gap-1">
                     <i class="pi pi-info-circle text-[10px]"></i>
                     <span>Average value generated per reservation</span>
                  </div>
               </div>
            </dl>
         </div>

         <!-- Growth Charts Grid -->
         <div v-if="user.type !== 'web'" class="grid grid-cols-1 lg:grid-cols-2 gap-8 mt-4">
            <!-- Bookings Growth Card -->
            <div class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
               <div class="flex items-center justify-between mb-6">
                  <div>
                     <h3 class="text-base font-bold text-gray-950">Bookings Growth</h3>
                     <p class="text-xs font-semibold text-gray-400 mt-0.5">Reservations volume over time</p>
                  </div>
                  <div>
                     <ListBox name="bookings_chart" :data="dataOptions" @selected="handleSelectedOption" />
                  </div>
               </div>
               <div v-if="bookingsData" class="h-[300px] flex items-center justify-center">
                  <BarChart :chartData="bookingsData" :options="{ responsive: true, maintainAspectRatio: false }" />
               </div>
            </div>

            <!-- Sales Growth Card -->
            <div class="bg-white rounded-2xl border border-gray-100 p-5 shadow-sm">
               <div class="flex items-center justify-between mb-6">
                  <div>
                     <h3 class="text-base font-bold text-gray-950">Sales Growth</h3>
                     <p class="text-xs font-semibold text-gray-400 mt-0.5">Revenue generation over time</p>
                  </div>
                  <div>
                     <ListBox name="sales_chart" :data="dataOptions" @selected="handleSelectedOption" />
                  </div>
               </div>
               <div v-if="salesData" class="h-[300px] flex items-center justify-center">
                  <BarChart :chartData="salesData" :options="{ responsive: true, maintainAspectRatio: false }" />
               </div>
            </div>
         </div>
      </main>
   </div>
   
    <Modal :show="openModal" :canClose="true" @closed="handleModalClose">
       <template v-slot:content>
          <div class="w-full max-w-md mx-auto bg-white rounded-3xl p-6 md:p-8 shadow-[0_12px_40px_rgba(0,0,0,0.12)] border border-slate-100 flex flex-col relative">
             <!-- Modal Header -->
             <div class="flex items-center justify-between mb-6 pb-4 border-b border-gray-100">
                <div class="text-left">
                   <h2 class="text-lg font-bold text-gray-900 tracking-tight">Request Loyalty Points Payout</h2>
                   <p class="text-xs text-gray-500 font-medium mt-1">Review bank details and confirm cashout points.</p>
                </div>
                <button @click="handleModalClose" class="text-gray-400 hover:text-gray-600 transition-colors p-1.5 hover:bg-gray-50 rounded-lg">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-5 h-5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12" />
                   </svg>
                </button>
             </div>

             <!-- Case 1: Bank details are missing in profile -->
             <div v-if="!agentProfileDetails || !agentProfileDetails.bank_name || !agentProfileDetails.bank_account_name || !agentProfileDetails.bank_account_number || !agentProfileDetails.country" 
                  class="flex flex-col items-center justify-center py-6 text-center">
                <div class="h-16 w-16 bg-rose-50 rounded-full flex items-center justify-center text-rose-500 mb-4 ring-8 ring-rose-50/50">
                   <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="2" stroke="currentColor" class="w-8 h-8">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                   </svg>
                </div>
                <h3 class="text-base font-bold text-gray-900">Missing Bank Details</h3>
                <p class="mt-2 text-sm text-gray-500 leading-relaxed max-w-xs font-regular">
                   Kindly fill the missing bank details in your profile first:
                   <span class="block mt-1 font-bold text-rose-600 tracking-wide">{{ missingBankFields.join(', ') }}</span>
                </p>
                <router-link to="/agent/settings" class="mt-6 px-5 py-3 bg-theme-active hover:bg-theme-active/95 text-white font-extrabold text-xs uppercase tracking-wider rounded-xl transition-all shadow-md active:scale-95 text-center">
                   Go to Profile Settings
                </router-link>
             </div>

             <!-- Case 2: Bank details exist, show values and withdraw points count input -->
             <div v-else class="flex flex-col gap-6">
                <!-- Bank Info Details Summary Preview -->
                <div class="bg-gray-50 rounded-2xl p-5 border border-gray-200/60 flex flex-col gap-3 text-left">
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Name</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_name }}</span>
                   </div>
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account Title</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_account_name }}</span>
                   </div>
                   <div class="flex justify-between border-b border-gray-200/40 pb-2">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Account / IBAN</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.bank_account_number }}</span>
                   </div>
                   <div class="flex justify-between pb-1">
                      <span class="text-xs font-semibold text-gray-400 uppercase tracking-wider">Bank Country</span>
                      <span class="text-sm font-bold text-gray-800">{{ agentProfileDetails.country || '-' }}</span>
                   </div>
                </div>

                <!-- Input points box -->
                <div class="flex flex-col gap-2 text-left">
                   <label for="withdraw-points" class="uppercase text-xs font-extrabold text-gray-400 tracking-wider">Points to Withdraw *</label>
                   <div class="relative rounded-xl border border-gray-200 flex items-center bg-gray-50 focus-within:bg-white focus-within:border-theme-active transition-all duration-200">
                      <input v-model="pointsToWithdraw" 
                             type="number" 
                             id="withdraw-points" 
                             :max="loyaltyData.points_earned"
                             placeholder="Enter points count" 
                             class="w-full bg-transparent outline-none p-3.5 font-bold text-gray-800 text-base" />
                      <span class="pr-4 text-xs font-bold text-emerald-600 whitespace-nowrap">
                         ${{ (Number(pointsToWithdraw || 0) * (Number(loyaltyData.redeem_rate) || 0.1)).toFixed(2) }} USD
                      </span>
                   </div>
                   <span v-if="pointsError" class="text-xs text-rose-600 font-semibold mt-1">{{ pointsError }}</span>
                   <p class="text-[11px] text-gray-400 mt-1 font-medium">Available loyalty points balance: <strong class="text-gray-700 font-bold">{{ loyaltyData.points_earned }}</strong></p>
                </div>

                <!-- Action Buttons -->
                <div class="flex items-center justify-end gap-3 mt-4">
                   <button type="button" @click="handleModalClose" class="px-5 py-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl text-xs font-bold text-gray-600 uppercase transition-all">Cancel</button>
                   <button type="button" @click="handleModalSubmit" :disabled="isBusy" class="px-5 py-2.5 bg-theme-active hover:bg-theme-active/95 text-white rounded-xl text-xs font-bold uppercase transition-all flex items-center gap-1.5 shadow-md disabled:opacity-50">
                      <svg v-if="isBusy" class="animate-spin h-3.5 w-3.5 text-white" fill="none" viewBox="0 0 24 24">
                         <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                         <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Confirm Payout
                   </button>
                </div>
                <p v-if="loyaltyRedeemForm.error" class="text-ef-error mt-2 text-left">{{ loyaltyRedeemForm.error }}</p>
             </div>
          </div>
       </template>
    </Modal>
   <Loader :show="isLoading" />
</template>

<script setup lang="ts">
import { computed, ref, watch, onMounted } from 'vue'
import { BarChart } from 'vue-chart-3'
import { Chart, registerables } from "chart.js"
import ListBox from '@/widgets/ListBox.vue'
import { logout, isLoggedIn, handleApiError, disableScroll, enableScroll } from '@/utils/common'
import Dashboard from '@/composables/admin/Dashboard'
import Loader from '@/widgets/Loader.vue'
import router from '@/router'
import 'flatpickr/dist/flatpickr.css'
import Modal from '@/widgets/Modal.vue'
import flatPickr from 'vue-flatpickr-component'
import { NotificationType } from '@/types/index'
import Agent from '@/composables/admin/agent'
import { LOADER_MINIMUM_TIME } from '@/utils/constants'

type ChartOption = 'daily' | 'monthly' | 'weekly'
const pages = [
  { name: 'Projects', href: '#', current: false },
  { name: 'Project Nero', href: '#', current: true },
]
const props = defineProps(['user', 'agent', 'breadcrump'])
Chart.register(...registerables)
const bookingsData = ref<any>(null)
const salesData = ref<any>(null)
const isLoading = ref<boolean>(true)
const timeElapsed = ref<number>(0)
const { fetchData }  = Dashboard()
const dates = ref<string | null>(null)
const subAgent = ref<any>(props.agent ? props.agent : '')
const auth = isLoggedIn()
// const loyaltyData = ref<any>()
const loyaltyData = ref<any>({
  points_earned: 0,
  total_points: 0,
  has_redeemed: 0
})
const openModal = ref<boolean>(false)
const isBusy = ref<boolean>(false)
const notif = ref<NotificationType>({ show: false, type: 'success', message: '', messageDetails: ''})
const { requestRedeemLoyalty, agentProfile } = Agent()
const loyaltyRedeemForm = ref<any>({
   bankName: { value: '', error: false, errorMessage: '' },
   accountName: { value: '', error: false, errorMessage: '' },
   accountNumber: { value: '', error: false, errorMessage: '' },
   countryName: { value: '', error: false, errorMessage: '' }
})

const dateRangeConfig : any = {
   mode: 'range',
   altFormat: 'd-m-Y',
   altInput: true,
   dateFormat: 'd-m-Y'
}

onMounted(async () => {

   disableScroll()
   const startTime = performance.now()
   await fetchAllData()
   timeElapsed.value = performance.now() - startTime
   if (timeElapsed.value < LOADER_MINIMUM_TIME) {
      const diff = LOADER_MINIMUM_TIME - timeElapsed.value
      setTimeout(() => {
         enableScroll()
         isLoading.value = false
      }, diff)
   } else {
      enableScroll()
      isLoading.value = false
   }
})

const agentProfileDetails = ref<any>(null)
const pointsToWithdraw = ref<number | null>(null)
const pointsError = ref<string>('')
const isProfileLoading = ref<boolean>(false)

const missingBankFields = computed(() => {
   const missing = []
   if (!agentProfileDetails.value) return ['Bank Name', 'Account Title', 'Account Number/IBAN', 'Country']
   if (!agentProfileDetails.value.bank_name) missing.push('Bank Name')
   if (!agentProfileDetails.value.bank_account_name) missing.push('Account Title')
   if (!agentProfileDetails.value.bank_account_number) missing.push('Account Number or IBAN')
   if (!agentProfileDetails.value.country) missing.push('Country')
   return missing
})

const handleRedeemPoints = async () => {
   isProfileLoading.value = true
   const profileRes = await agentProfile()
   isProfileLoading.value = false
   if (profileRes.status === 200) {
      agentProfileDetails.value = profileRes.data.data
      
      loyaltyRedeemForm.value.bankName.value = profileRes.data.data.bank_name || ''
      loyaltyRedeemForm.value.accountName.value = profileRes.data.data.bank_account_name || ''
      loyaltyRedeemForm.value.accountNumber.value = profileRes.data.data.bank_account_number || ''
      loyaltyRedeemForm.value.countryName.value = profileRes.data.data.country || ''
      
      pointsToWithdraw.value = loyaltyData.value ? Math.floor(Number(loyaltyData.value.points_earned)) : 0
   }
   openModal.value = true
}

const handleModalClose = () => openModal.value = false

const scopes = ref<any>(
   [
      {id: 'today', name: 'Today', isCurrent: false},
      {id: 'yesterday', name: 'Yesterday', isCurrent: false},
      {id: 'last_7_days', name: 'Last 7 Days', isCurrent: false},
      {id: 'this_month', name: 'This Month', isCurrent: true},
      {id: 'last_month', name: 'Last Month', isCurrent: false},
      {id: 'all_time', name: 'All Time', isCurrent: false},
      {id: 'custom', name: 'Custom Duration', isCurrent: false}
   ]
)

const nonCustomScopes = computed(() => scopes.value.filter((s: any) => !s.id.includes('custom')))
const isCustomScopeCurrent = computed(() => scopes.value.find((s: any) => s.id.includes('custom'))?.isCurrent)

const validateForm = () : boolean => {
   pointsError.value = ''
   let response = true
   
   if (!pointsToWithdraw.value || pointsToWithdraw.value <= 0) {
      pointsError.value = 'Please enter a valid points amount'
      response = false
   } else if (loyaltyData.value && pointsToWithdraw.value > Number(loyaltyData.value.points_earned)) {
      pointsError.value = 'Cannot exceed your accumulated points'
      response = false
   }
   
   return response
}

const handleModalSubmit = async () => {

   const isValid = validateForm()

   if (isValid && !isBusy.value && auth && auth.user) {
      isBusy.value = true
      const response = await requestRedeemLoyalty(loyaltyRedeemForm.value, pointsToWithdraw.value || 0)
      isBusy.value = false

      if (response.status == 200) {
         openModal.value = false
         await fetchAllData()
      } else {
         loyaltyRedeemForm.value.error = handleApiError(response)
      }
   }
}

const dataOptions = [
   { title: 'Daily', description: '', value: 'daily', current: true },
   { title: 'Weekly', description: '', value: 'weekly', current: false },
   { title: 'Monthly', description: '', value: 'monthly', current: false },
]

const bookingStats = ref<any>({
      total: { id: 1, name: 'Confirmed / Total Bookings', stat: 0 },
      confirmed: { id: 2, name: 'Paid', stat: 0 },
      pending: { id: 3, name: 'Unpaid', stat: 0 },
      subAgents: { id: 4, name: 'Online Bookings', stat: 0 },
      cancelled: { id: 5, name: 'Cancelled Bookings', stat: 0 }
})

const notifClosed = () : void => {
   notif.value.show = false
}

const salesStats = ref<any>(
   {
      total: { id: 1, name: 'Total Sales', stat: 0 },
      commission: { id: 2, name: 'Total Commission', stat: 0 },
      pending: { id: 3, name: 'Pending Payments', stat: 0 },
      average: { id: 4, name: 'Avg Booking Value', stat: 0 }
   }
)

const handleSelectedOption = async (name: string, value: ChartOption) => {

   await fetchChartData(name, value) 
}

const redirectBookings = (status = '') => {
   const query = status ? { status } : {}
   if (props.user.type == 'admin') {
      router.push({name: 'AdminBookings', query})
   } else if (props.user.type == 'agent') {
      router.push({name: 'UserBookings', params: {userType: 'agent', subUserId: ''}, query})
   } else if (props.user.type == 'corporate') {
      router.push({name: 'UserBookings', params: {userType: 'corporate', subUserId: ''}, query})
   } else if (props.user.type == 'web') {
      router.push({name: 'UserBookings', params: {userType: 'web', subUserId: ''}, query})
   }
}

const fetchChartData = async (chartName: string, option: ChartOption) => {

   const response = await fetchData(chartName, option, subAgent.value)

   if (response.status == 200) {
      
      if (chartName == 'bookings_chart') {

         prepareChartData('bookings', response)
      } else if (chartName == 'sales_chart') {
         
         prepareChartData('sales', response)
      }
   }
}

const fetchAllData = async () => {
  const activeScope = scopes.value.find((scope: any) => scope.isCurrent)
  const scopeId = activeScope ? activeScope.id : 'this_month'
  const response = await fetchData('all', scopeId, subAgent.value)

  if (response.status == 200) {
    prepareChartData('bookings', response)
    prepareChartData('sales', response)
    prepareBookingStats(response)
    prepareSalesStats(response)

    // ✅ safe read (if backend omits the node, keep the default)
    loyaltyData.value = response.data?.loyalty_summary ?? loyaltyData.value
  } else if (response.response && response.response.status == 401) {
    logout(router)
  } else {
    // ✅ non-200 and not 401: keep the default object
    loyaltyData.value = loyaltyData.value
  }
}

const fetchStats = async () => {

   const activeScope = scopes.value.find((scope: any) => scope.isCurrent)

   if (activeScope) {

      const response = await fetchData('stats', activeScope.id, subAgent.value)
      if (response.status == 200) {
         
         prepareBookingStats(response)
         prepareSalesStats(response)
      }
   }
}

const prepareChartData = (chartName: string, response: any) => {

   if (chartName == 'bookings') {

      bookingsData.value = {
         labels: response.data.bookings_chart.data.labels,
         datasets: [
            {
               label: 'Bookings',
               data: response.data.bookings_chart.data.sets,
               backgroundColor: ['#77CEFF', '#0079AF', '#123E6B', '#97B0C4', '#A5C8ED', '#77CEFF','#77CEFF','#77CEFF','#77CEFF'],
            },
         ]
      }
   } else if (chartName == 'sales') {

      salesData.value = {
         labels: response.data.sales_chart.data.labels,
         datasets: [
            {
               label: 'Sales',
               data: response.data.sales_chart.data.sets,
               backgroundColor: ['#f87979', '#f87979', '#f87979', '#f87979', '#f87979', '#f87979','#f87979','#f87979','#f87979'],
            },
         ]
      }
   }
}

const prepareBookingStats = (response: any) => {

   bookingStats.value.total.stat = response.data.bookings_summary.total_bookings
   bookingStats.value.confirmed.stat = response.data.bookings_summary.confirmed_bookings
   bookingStats.value.pending.stat = response.data.bookings_summary.pending_bookings
   bookingStats.value.subAgents.stat = response.data.bookings_summary.sub_agent_bookings
   bookingStats.value.cancelled.stat = response.data.bookings_summary.cancelled_bookings
}

const prepareSalesStats = (response: any) => {

   salesStats.value.total.stat = response.data.sales_summary.total_sales
   salesStats.value.commission.stat = response.data.sales_summary.commission
   salesStats.value.pending.stat = response.data.sales_summary.pending_sales
   salesStats.value.average.stat = response.data.sales_summary.average
}

const handleScopeUpdate = async (item: any) => {

   scopes.value.forEach((scope: any) => {
      scope.isCurrent = false
   })

   scopes.value.find((scope: any) => scope.id === item.id).isCurrent = true
   dates.value = ''

   await fetchStats()
}

watch(() => props.agent, (newVallue, oldValue) => {
   
   subAgent.value = newVallue
   refetchData()
})

const refetchData = async () => {
   await fetchAllData()
}

watch(dates, (newValue, oldValue) => {

   if (newValue?.includes('to')) {
      
      scopes.value.forEach((scope: any) => {
         if (!scope.id.includes('custom')) {
            scope.isCurrent = false
         } else {
            scope.isCurrent = true
            const dateRange = `${newValue.split(' to ')[0]}_${newValue.split(' to ')[1]}`
            scope.id = `custom_${dateRange}`
         }
      })

      fetchStats().then(() => {
         
      })
   }
})

</script>